import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/hooks/queryKeys";
import { useUiStore } from "@/stores/ui.store";
import { MAX_LENGTHS, isValidEmail } from "@/lib/validation";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Typography } from "@/components/ui/Typography";
import { AddressMapSearch } from "@/components/AddressMapSearch";
import { Card, CardContent } from "@/components/ui/Card";

interface TenantForm {
  name: string;
  phone: string;
  email: string;
  securityDeposit: string;
  agreementEndDate: string;
  noticePeriodDays: string;
}

const emptyTenant: TenantForm = {
  name: "",
  phone: "",
  email: "",
  securityDeposit: "",
  agreementEndDate: "",
  noticePeriodDays: "30",
};

const UNIT_TYPES = [
  { value: "STUDIO", label: "Studio" },
  { value: "ONE_BHK", label: "1 BHK" },
  { value: "TWO_BHK", label: "2 BHK" },
  { value: "THREE_BHK", label: "3 BHK" },
  { value: "FOUR_BHK", label: "4 BHK" },
  { value: "OTHER", label: "Other" },
] as const;

interface UnitForm {
  alias: string;
  unitNumber: string;
  unitType: string;
  maxTenants: string;
  rentAmount: string;
  dueDate: string;
  securityDeposit: string;
  agreementEndDate: string;
  noticePeriodDays: string;
  notes: string;
  tenants: TenantForm[];
}

const emptyUnit: UnitForm = {
  alias: "",
  unitNumber: "",
  unitType: "ONE_BHK",
  maxTenants: "1",
  rentAmount: "",
  dueDate: "1",
  securityDeposit: "",
  agreementEndDate: "",
  noticePeriodDays: "30",
  notes: "",
  tenants: [],
};

export function AddPropertyPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addToast = useUiStore((s) => s.addToast);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showRoomForm, setShowRoomForm] = useState(false);

  const PROPERTY_TYPES = [
    { value: "APARTMENT", label: "Apartment" },
    { value: "INDEPENDENT_HOUSE", label: "Independent House" },
    { value: "VILLA", label: "Villa" },
    { value: "PLOT", label: "Plot" },
    { value: "COMMERCIAL", label: "Commercial" },
    { value: "OTHER", label: "Other" },
  ] as const;

  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    propertyType: "OTHER" as string,
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
    units: [] as UnitForm[],
  });

  function update(field: string, value: string | number | undefined) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }

  function updateUnit(index: number, field: keyof UnitForm, value: string | TenantForm[]) {
    setForm((prev) => ({
      ...prev,
      units: prev.units.map((u, i) =>
        i === index ? { ...u, [field]: value } : u
      ),
    }));
    setErrors((prev) => ({ ...prev, [`unit_${index}_${field}`]: "" }));
  }

  function updateUnitTenant(unitIndex: number, tenantIndex: number, field: keyof TenantForm, value: string) {
    setForm((prev) => ({
      ...prev,
      units: prev.units.map((u, ui) =>
        ui === unitIndex
          ? {
              ...u,
              tenants: u.tenants.map((t, ti) =>
                ti === tenantIndex ? { ...t, [field]: value } : t
              ),
            }
          : u
      ),
    }));
    setErrors((prev) => ({ ...prev, [`unit_${unitIndex}_tenant_${tenantIndex}_${field}`]: "" }));
  }

  function addUnit() {
    setForm((prev) => ({ ...prev, units: [...prev.units, { ...emptyUnit }] }));
    setShowRoomForm(true);
  }

  function removeUnit(index: number) {
    setForm((prev) => ({
      ...prev,
      units: prev.units.filter((_, i) => i !== index),
    }));
  }

  function addTenant(unitIndex: number) {
    setForm((prev) => ({
      ...prev,
      units: prev.units.map((u, i) =>
        i === unitIndex ? { ...u, tenants: [...u.tenants, { ...emptyTenant }] } : u
      ),
    }));
  }

  function removeTenant(unitIndex: number, tenantIndex: number) {
    setForm((prev) => ({
      ...prev,
      units: prev.units.map((u, i) =>
        i === unitIndex
          ? { ...u, tenants: u.tenants.filter((_, ti) => ti !== tenantIndex) }
          : u
      ),
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = "Property name is required";
    else if (form.name.length > MAX_LENGTHS.propertyName) newErrors.name = `Property name must be at most ${MAX_LENGTHS.propertyName} characters`;
    if (!form.address.trim()) newErrors.address = "Address is required";
    else if (form.address.length > MAX_LENGTHS.address) newErrors.address = `Address must be at most ${MAX_LENGTHS.address} characters`;
    if (form.city.length > MAX_LENGTHS.city) newErrors.city = `City must be at most ${MAX_LENGTHS.city} characters`;
    if (form.state.length > MAX_LENGTHS.state) newErrors.state = `State must be at most ${MAX_LENGTHS.state} characters`;
    if (form.pincode.length > MAX_LENGTHS.pincode) newErrors.pincode = `Pincode must be at most ${MAX_LENGTHS.pincode} characters`;

    form.units.forEach((unit, ui) => {
      if (!unit.alias.trim()) newErrors[`unit_${ui}_alias`] = "Unit alias is required";
      else if (unit.alias.length > MAX_LENGTHS.unitAlias) newErrors[`unit_${ui}_alias`] = `Unit alias must be at most ${MAX_LENGTHS.unitAlias} characters`;
      if (!unit.unitNumber.trim()) newErrors[`unit_${ui}_unitNumber`] = "Unit number is required";
      else if (unit.unitNumber.length > MAX_LENGTHS.unitNumber) newErrors[`unit_${ui}_unitNumber`] = `Unit number must be at most ${MAX_LENGTHS.unitNumber} characters`;
      if (!unit.rentAmount || parseFloat(unit.rentAmount) <= 0)
        newErrors[`unit_${ui}_rentAmount`] = "Valid rent amount is required";
      const dueDate = parseInt(unit.dueDate, 10);
      if (dueDate < 1 || dueDate > 31) newErrors[`unit_${ui}_dueDate`] = "Due date must be 1–31";
      const maxTenants = parseInt(unit.maxTenants, 10);
      if (!maxTenants || maxTenants < 1 || maxTenants > 20) newErrors[`unit_${ui}_maxTenants`] = "Max tenants must be 1–20";
      if (unit.tenants.length > maxTenants) newErrors[`unit_${ui}_maxTenants`] = `This unit allows max ${maxTenants} tenant(s).`;
      if (unit.notes.length > MAX_LENGTHS.notes) newErrors[`unit_${ui}_notes`] = `Notes must be at most ${MAX_LENGTHS.notes} characters`;
      unit.tenants.forEach((t, ti) => {
        if (!t.name.trim()) newErrors[`unit_${ui}_tenant_${ti}_name`] = "Tenant name is required";
        if (!t.phone.trim() || t.phone.length < 10)
          newErrors[`unit_${ui}_tenant_${ti}_phone`] = "Valid phone number is required";
        if (t.email.trim() && !isValidEmail(t.email))
          newErrors[`unit_${ui}_tenant_${ti}_email`] = "Invalid email address";
      });
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const body = {
        name: form.name.trim(),
        address: form.address.trim(),
        city: form.city.trim() || undefined,
        state: form.state.trim() || undefined,
        pincode: form.pincode.trim() || undefined,
        propertyType: form.propertyType !== "OTHER" ? form.propertyType : undefined,
        latitude: form.latitude,
        longitude: form.longitude,
        units: form.units.map((u) => ({
          alias: u.alias.trim(),
          unitNumber: u.unitNumber.trim(),
          unitType: u.unitType,
          maxTenants: parseInt(u.maxTenants, 10) || 1,
          rentAmount: parseFloat(u.rentAmount),
          dueDate: parseInt(u.dueDate, 10) || 1,
          securityDeposit: u.securityDeposit ? parseFloat(u.securityDeposit) : undefined,
          agreementEndDate: u.agreementEndDate || undefined,
          noticePeriodDays: parseInt(u.noticePeriodDays, 10) || 30,
          notes: u.notes.trim() || undefined,
          tenants: u.tenants.map((t) => ({
            name: t.name.trim(),
            phone: t.phone.trim(),
            email: t.email.trim() || undefined,
            securityDeposit: t.securityDeposit ? parseFloat(t.securityDeposit) : undefined,
            agreementEndDate: t.agreementEndDate || undefined,
            noticePeriodDays: parseInt(t.noticePeriodDays, 10) || 30,
          })),
        })),
      };
      await api.post("/properties", body);
      queryClient.invalidateQueries({ queryKey: queryKeys.properties() });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardSummary() });
      addToast({ type: "success", message: "Property added successfully" });
      navigate("/properties");
    } catch (err) {
      addToast({
        type: "error",
        message: err instanceof ApiError ? err.message : "Failed to add property",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <Link
          to="/properties"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-2"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Back to properties
        </Link>
        <Typography variant="h1">Add Property</Typography>
        <p className="mt-1 text-sm text-slate-500">
          Add a property. You can add units and tenants now or later from the property view.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardContent className="space-y-4">
            <Typography variant="h2">Property details</Typography>
            <Input
              label="Property name"
              tooltip="A short name for this property (e.g. building or block name)."
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              error={errors.name}
              placeholder="e.g. Green Apartments"
              maxLength={MAX_LENGTHS.propertyName}
              required
            />
            <AddressMapSearch
              label="Address"
              value={form.address}
              latitude={form.latitude}
              longitude={form.longitude}
              onChange={(r) => {
                update("address", r.address);
                update("latitude", r.latitude);
                update("longitude", r.longitude);
              }}
              error={errors.address}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="City"
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                placeholder="e.g. Bangalore"
                maxLength={MAX_LENGTHS.city}
                error={errors.city}
              />
              <Input
                label="State"
                value={form.state}
                onChange={(e) => update("state", e.target.value)}
                placeholder="e.g. Karnataka"
                maxLength={MAX_LENGTHS.state}
                error={errors.state}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Pincode"
                value={form.pincode}
                onChange={(e) => update("pincode", e.target.value)}
                placeholder="e.g. 560001"
                maxLength={MAX_LENGTHS.pincode}
                error={errors.pincode}
              />
              <Select
                label="Property type"
                options={PROPERTY_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                value={form.propertyType}
                onChange={(e) => update("propertyType", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Typography variant="h2">Units</Typography>
              {!showRoomForm && form.units.length === 0 ? (
                <Button type="button" variant="secondary" size="sm" onClick={addUnit}>
                  + Add unit details
                </Button>
              ) : (
                <Button type="button" variant="secondary" size="sm" onClick={addUnit}>
                  + Add unit
                </Button>
              )}
            </div>
            <Typography variant="helper" as="p" className="text-sm">
              Optional. Add units with type (e.g. 1 BHK, 2 BHK), max tenants, and rent. Each unit can have tenants up to the max.
            </Typography>
            {showRoomForm && form.units.map((unit, unitIndex) => (
              <div
                key={unitIndex}
                className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/50 dark:bg-slate-800/50 p-4 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600">Unit {unitIndex + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeUnit(unitIndex)}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Remove unit
                  </button>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Unit alias"
                    value={unit.alias}
                    onChange={(e) => updateUnit(unitIndex, "alias", e.target.value)}
                    error={errors[`unit_${unitIndex}_alias`]}
                    placeholder="e.g. Ground floor, Block A"
                    maxLength={MAX_LENGTHS.unitAlias}
                  />
                  <Input
                    label="Unit number"
                    value={unit.unitNumber}
                    onChange={(e) => updateUnit(unitIndex, "unitNumber", e.target.value)}
                    error={errors[`unit_${unitIndex}_unitNumber`]}
                    placeholder="e.g. 101, A-1"
                    maxLength={MAX_LENGTHS.unitNumber}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label="Property type"
                    options={UNIT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                    value={unit.unitType}
                    onChange={(e) => updateUnit(unitIndex, "unitType", e.target.value)}
                  />
                  <Input
                    label="Max tenants"
                    type="number"
                    min="1"
                    max="20"
                    value={unit.maxTenants}
                    onChange={(e) => updateUnit(unitIndex, "maxTenants", e.target.value)}
                    error={errors[`unit_${unitIndex}_maxTenants`]}
                    placeholder="1"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Monthly rent (₹)"
                    type="number"
                    min="1"
                    value={unit.rentAmount}
                    onChange={(e) => updateUnit(unitIndex, "rentAmount", e.target.value)}
                    error={errors[`unit_${unitIndex}_rentAmount`]}
                    placeholder="15000"
                  />
                  <Input
                    label="Rent due date (1–31)"
                    type="number"
                    min="1"
                    max="31"
                    value={unit.dueDate}
                    onChange={(e) => updateUnit(unitIndex, "dueDate", e.target.value)}
                    error={errors[`unit_${unitIndex}_dueDate`]}
                    placeholder="1"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Security deposit (₹)"
                    type="number"
                    min="0"
                    value={unit.securityDeposit}
                    onChange={(e) => updateUnit(unitIndex, "securityDeposit", e.target.value)}
                    placeholder="Optional"
                  />
                  <Input
                    label="Agreement end date"
                    type="date"
                    value={unit.agreementEndDate}
                    onChange={(e) => updateUnit(unitIndex, "agreementEndDate", e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <Input
                  label="Notes"
                  value={unit.notes}
                  onChange={(e) => updateUnit(unitIndex, "notes", e.target.value)}
                  placeholder="Optional unit notes"
                  maxLength={MAX_LENGTHS.notes}
                  error={errors[`unit_${unitIndex}_notes`]}
                />

                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Tenants in this unit (max {unit.maxTenants})</span>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => addTenant(unitIndex)}
                      disabled={unit.tenants.length >= parseInt(unit.maxTenants, 10) || false}
                    >
                      + Add tenant
                    </Button>
                  </div>
                  {unit.tenants.map((tenant, tenantIndex) => (
                    <div
                      key={tenantIndex}
                      className="rounded-lg border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800/80 p-3 mb-3 space-y-3"
                    >
                      <div className="flex justify-between">
                        <span className="text-xs font-medium text-slate-500">Tenant {tenantIndex + 1}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeTenant(unitIndex, tenantIndex);
                          }}
                          className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                        >
                          Remove
                        </button>
                      </div>
                      <Input
                        label="Name"
                        value={tenant.name}
                        onChange={(e) => updateUnitTenant(unitIndex, tenantIndex, "name", e.target.value)}
                        error={errors[`unit_${unitIndex}_tenant_${tenantIndex}_name`]}
                        placeholder="Full name"
                      />
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Input
                          label="Phone"
                          type="tel"
                          value={tenant.phone}
                          onChange={(e) => updateUnitTenant(unitIndex, tenantIndex, "phone", e.target.value)}
                          error={errors[`unit_${unitIndex}_tenant_${tenantIndex}_phone`]}
                          placeholder="10-digit mobile"
                        />
                        <Input
                          label="Email"
                          type="email"
                          value={tenant.email}
                          onChange={(e) => updateUnitTenant(unitIndex, tenantIndex, "email", e.target.value)}
                          placeholder="Optional"
                          error={errors[`unit_${unitIndex}_tenant_${tenantIndex}_email`]}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex flex-row flex-wrap gap-3 items-center justify-end">
          <Link to="/properties">
            <Button variant="danger" type="button">
              Cancel
            </Button>
          </Link>
          <Button type="submit" isLoading={loading}>
            Add Property
          </Button>
        </div>
      </form>
    </div>
  );
}
